import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Card,
  Tag,
  Tooltip,
  Row,
  Col,
  Typography,
  Empty,
  Divider,
  Statistic,
  Progress,
  Badge,
  Alert,
  Spin,
  Radio,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  GlobalOutlined,
  SettingOutlined,
  BarChartOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  UploadOutlined,
  CopyOutlined,
  ShareAltOutlined,
  LockOutlined,
  UnlockOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { domainService, Domain, DomainStatus, DOMAIN_STATUS_CONFIG } from '../services/domain';
import { userApi, User } from '../services/user';
import { useNavigate } from 'react-router-dom';
import { DOMAIN_PERMISSIONS, SYSTEM_PERMISSIONS } from '../constants/permissions';
import { PermissionGuard } from '../components/PermissionGuard';
import { useAuthStore } from '../stores/authStore';

const { Text, Title } = Typography;
const { Option } = Select;

const DomainManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.user_type === 'admin';
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDomain, setEditingDomain] = useState<Domain | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assigningDomain, setAssigningDomain] = useState<Domain | null>(null);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignForm] = Form.useForm();
  const [clients, setClients] = useState<User[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  // 防抖和取消请求的引用
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 加载域名数据（支持搜索）
  const loadDomains = useCallback(async (keyword = '', page = 1, size = 10) => {
    setLoading(true);
    
    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 创建新的请求控制器
    abortControllerRef.current = new AbortController();
    
    try {
      const response = await domainService.getDomainsList({
        keyword,
        page,
        size,
      });
      
      if (response && response.success) {
        setDomains(response.data.list);
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('加载域名列表失败:', error);
        message.error('加载域名列表失败');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // 防抖搜索
  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // 设置新的定时器
    debounceTimerRef.current = setTimeout(() => {
      loadDomains(value);
    }, 300);
  }, [loadDomains]);

  const loadClients = useCallback(async () => {
    if (!isAdmin) {
      return;
    }

    setClientsLoading(true);
    try {
      const response = await userApi.getClientUsers({
        page: 1,
        size: 100,
        user_type: 'client',
      });
      if (response?.success) {
        setClients(response.data?.list || []);
      }
    } catch (error) {
      console.error('加载客户列表失败:', error);
      message.error('加载客户列表失败');
    } finally {
      setClientsLoading(false);
    }
  }, [isAdmin]);

  // 初始加载
  useEffect(() => {
    loadDomains();
    
    // 清理函数
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadDomains]);

  useEffect(() => {
    if (isAdmin) {
      loadClients();
    }
  }, [isAdmin, loadClients]);

  // 创建域名
  const handleCreate = () => {
    setEditingDomain(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 编辑域名
  const handleEdit = (domain: Domain) => {
    setEditingDomain(domain);
    form.setFieldsValue({
      domain_name: domain.domain_name,
      description: domain.description,
      status: domain.status,
    });
    setModalVisible(true);
  };

  // 删除域名
  const handleDelete = (domain: Domain) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除域名 "${domain.domain_name}" 吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await domainService.deleteDomain({ id: domain.id });
          message.success('域名删除成功');
          loadDomains(searchText);
        } catch (error) {
          console.error('删除域名失败:', error);
          message.error('删除域名失败');
        }
      },
    });
  };

  // 切换域名状态
  const handleToggle = async (domain: Domain) => {
    try {
      await domainService.toggleDomain({ id: domain.id });
      message.success('域名状态切换成功');
      loadDomains(searchText);
    } catch (error) {
      console.error('切换域名状态失败:', error);
      message.error('切换域名状态失败');
    }
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingDomain) {
        // 更新域名
        await domainService.updateDomain({
          id: editingDomain.id,
          ...values,
        });
        message.success('域名更新成功');
      } else {
        // 创建域名
        await domainService.createDomain(values);
        message.success('域名创建成功');
      }
      
      setModalVisible(false);
      loadDomains(searchText);
    } catch (error) {
      console.error('提交失败:', error);
      message.error('操作失败');
    }
  };

  const handleAssignClick = useCallback((domain: Domain) => {
    setAssigningDomain(domain);
    assignForm.setFieldsValue({
      client_id: domain.client_id || undefined,
    });
    setAssignModalVisible(true);
  }, [assignForm]);

  const handleAssignSubmit = useCallback(async () => {
    if (!assigningDomain) {
      return;
    }

    try {
      const values = await assignForm.validateFields();
      setAssignSubmitting(true);
      await domainService.assignDomain({
        id: assigningDomain.id,
        client_id: values.client_id,
      });
      message.success('域名分配成功');
      setAssignModalVisible(false);
      setAssigningDomain(null);
      assignForm.resetFields();
      loadDomains(searchText);
    } catch (error) {
      console.error('分配域名失败:', error);
      message.error('分配域名失败');
    } finally {
      setAssignSubmitting(false);
    }
  }, [assignForm, assigningDomain, loadDomains, searchText]);

  const handleAssignCancel = useCallback(() => {
    setAssignModalVisible(false);
    setAssigningDomain(null);
    assignForm.resetFields();
  }, [assignForm]);

  // 获取状态配置
  const getStatusConfig = (status: DomainStatus) => {
    return DOMAIN_STATUS_CONFIG[status] || DOMAIN_STATUS_CONFIG[DomainStatus.ACTIVE];
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a: Domain, b: Domain) => a.id - b.id,
    },
    {
      title: '域名名称',
      dataIndex: 'domain_name',
      key: 'domain_name',
      ellipsis: true,
      render: (text: string, record: Domain) => (
        <Space>
          <GlobalOutlined />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: DomainStatus) => {
        const config = getStatusConfig(status);
        return (
          <Tag color={config.color} icon={<config.icon />}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString(),
    },
    // {
    //   title: '操作',
    //   key: 'action',
    //   width: 200,
    //   render: (_: any, record: Domain) => (
    //     <Space size="small">
    //       <PermissionGuard permission={DOMAIN_PERMISSIONS.READ}>
    //         <Tooltip title="查看详情">
    //           <Button
    //             type="text"
    //             icon={<EyeOutlined />}
    //             onClick={() => handleEdit(record)}
    //           />
    //         </Tooltip>
    //       </PermissionGuard>
          
    //       {isAdmin && (
    //         <>
    //           <PermissionGuard permission={DOMAIN_PERMISSIONS.UPDATE}>
    //             <Tooltip title="编辑">
    //               <Button
    //                 type="text"
    //                 icon={<EditOutlined />}
    //                 onClick={() => handleEdit(record)}
    //               />
    //             </Tooltip>
    //           </PermissionGuard>
              
    //           <PermissionGuard permission={DOMAIN_PERMISSIONS.DELETE}>
    //             <Tooltip title="删除">
    //               <Button
    //                 type="text"
    //                 danger
    //                 icon={<DeleteOutlined />}
    //                 onClick={() => handleDelete(record)}
    //               />
    //             </Tooltip>
    //           </PermissionGuard>
    //         </>
    //       )}
    //     </Space>
    //   ),
    // },
  ];

  if (isAdmin) {
    columns.splice(2, 0, {
      title: '客户账号',
      dataIndex: 'client_name',
      key: 'client_name',
      ellipsis: true,
      render: (text: string) => text || '-',
    });

    columns.push({
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_: any, record: Domain) => (
        <Space size="small">
          <Tooltip title="分配给客户账号">
            <Button
              type="text"
              icon={<UserSwitchOutlined />}
              onClick={() => handleAssignClick(record)}
            />
          </Tooltip>
        </Space>
      ),
    });
  }

  return (
    <div style={{ padding: '24px', background: 'var(--color-bg-tertiary)', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{
        background: 'var(--color-bg-primary)',
        padding: '24px 32px',
        borderRadius: 'var(--border-radius-lg)',
        marginBottom: '24px',
        boxShadow: 'var(--box-shadow-sm)',
        border: '1px solid var(--color-border-light)'
      }}>
        <Title level={2} style={{ margin: 0, marginBottom: '8px', fontSize: '28px', fontWeight: 700 }}>域名管理</Title>
        <Text type="secondary" style={{ fontSize: '14px' }}>管理所有域名的配置和状态</Text>
      </div>

      <Card
        bordered={false}
        style={{
          borderRadius: 'var(--border-radius-lg)',
          boxShadow: 'var(--box-shadow-sm)',
          border: '1px solid var(--color-border-light)'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <div style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <Input.Search
              placeholder="搜索域名名称或描述"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
            
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadDomains(searchText)}
              loading={loading}
            >
              刷新
            </Button>
          </div>

          {isAdmin && (
            <PermissionGuard permission={DOMAIN_PERMISSIONS.CREATE}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                创建域名
              </Button>
            </PermissionGuard>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={domains}
          rowKey="id"
          loading={loading}
          pagination={{
            total: domains.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* 创建/编辑域名模态框 */}
      <Modal
        title={editingDomain ? '编辑域名' : '创建域名'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: DomainStatus.ACTIVE,
          }}
        >
          <Form.Item
            label="域名名称"
            name="domain_name"
            rules={[
              { required: true, message: '请输入域名名称' },
              { pattern: /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: '请输入有效的域名格式' }
            ]}
          >
            <Input placeholder="例如: example.com" />
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
          >
            <Input.TextArea
              placeholder="请输入域名描述"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            label="状态"
            name="status"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Radio.Group>
              <Radio.Button value={DomainStatus.ACTIVE}>
                <CheckCircleOutlined /> 生效
              </Radio.Button>
              <Radio.Button value={DomainStatus.INACTIVE}>
                <CloseCircleOutlined /> 未生效
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* 域名分配模态框（管理员） */}
      {isAdmin && (
        <Modal
          title={assigningDomain ? `分配域名：${assigningDomain.domain_name}` : '分配域名'}
          open={assignModalVisible}
          onOk={handleAssignSubmit}
          onCancel={handleAssignCancel}
          confirmLoading={assignSubmitting}
          destroyOnClose
        >
          <Form form={assignForm} layout="vertical">
            <Form.Item
              label="客户账号"
              name="client_id"
              rules={[{ required: true, message: '请选择客户账号' }]}
            >
              <Select
                placeholder="请选择客户账号"
                loading={clientsLoading}
                showSearch
                optionFilterProp="children"
              >
                {clients.map((client) => (
                  <Option key={client.id} value={client.id}>
                    {client.username}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default DomainManagement;
